export enum Language {
  English = 'en',
  Russian = 'ru',
  German = 'de',
  Italian = 'it',
  Polish = 'pl',
  Armenian = 'hy',
}

export type VoiceType = 'male' | 'female'
export type VoiceGender = 'male' | 'female' | 'any'

export const studyModes = ['listen', 'translate'] as const
export type StudyMode = (typeof studyModes)[number]

export interface StudySelectors {
  level: number
  unit: number | null
  lesson: number | null
  slide: number | null
}

export type StudySelectorsByMode = Record<StudyMode, StudySelectors>

export interface UserSettings {
  interface_language: Language
  timezone?: string
  email_digest_paused?: boolean
}

export interface ProfileSettings {
  native_language: Language
  target_language: Language
  transliterations: string[]
  study_selectors?: StudySelectorsByMode
  email_digest_enabled?: boolean
}

export const transliterationRepresentationIds = ['ipa', 'cyrillic', 'latin'] as const

export interface Profile {
  settings: ProfileSettings
}

export interface SavedProfile extends Profile {
  profile_id: number
  user_id: number
}

export interface Memory {
  memory_id: number
  profile_id: number
  memory_text: string
  saved_at: Date
}

export interface ImageDescription {
  short: string
  full: string
}

export type MaterialData = Array<{text: string; image: string; description?: ImageDescription}>

export interface Material {
  material_id: number
  level: number
  unit: number
  lesson: number
  slide: number
  data: MaterialData
  lang: Language
}

export type TranslatedMaterialData = Array<{
  default: string
  commented: string | null
  error?: string | null
}>

export interface TranslatedMaterial {
  translate_id: number
  material_id: number
  lang: Language
  title: string
  status: 'check' | 'ok' | 'sample'
  data: TranslatedMaterialData
}

export enum Mode {
  Listen = 'listen',
  Translate = 'translate',
}

export interface Progress {
  progress_id: number
  profile_id: number
  material_id: number
  mode: string
  result: number | null
  comment: string | null
}

export interface Card {
  image: string
  text: string
  commented: string | null
  answer: string | null
}

export interface SearchResultItem {
  material_id: number
  title: string
  level: number
  unit: number
  lesson: number
  slide: number
  status: 'check' | 'ok' | 'sample'
  sources?: string[]
  translations?: string[]
}

export interface SearchResult {
  items: SearchResultItem[]
  total: number
}

export interface StudyCard {
  text: string
  image: string
  default: string
  commented: string | null
  voice_gender: VoiceGender
  voice_profile: string | null
}

export interface StudySlideData {
  material_id: number
  level: number
  unit: number
  lesson: number
  slide: number
  title: string
  cards: StudyCard[]
}

export interface StudyCardsResponse {
  material: {material_id: number; level: number; unit: number; lesson: number; slide: number}
  slide: StudySlideData
  lesson: {level: number; unit: number; lesson: number; slides: StudySlideData[]}
}

export enum Grade {
  Again = 1,
  Hard = 2,
  Good = 3,
  Easy = 4,
}

export interface CardProgress {
  progress_id: number
  profile_id: number
  card_key: string
  mode: StudyMode
  material_id: number | null
  phrase_index: number | null
  lang: string
  state: number
  stability: number
  difficulty: number
  due: string
  elapsed_days: number
  scheduled_days: number
  reps: number
  lapses: number
  last_grade: number | null
  history: unknown | null
  created_at: string
  updated_at: string
}

export interface CardsDeckProgress {
  n: number
  m: number
  percent: number
  breakdown: {new: number; learning: number; review: number; relearning: number}
}

export interface CardsModuleSideProgress {
  n: number
  m: number
}

export interface CardsModuleProgress {
  level: number
  unit: number
  listen: CardsModuleSideProgress
  translate: CardsModuleSideProgress
}

export interface CardsDeckResponse {
  cards: Array<{
    card_key: string
    material_id: number | null
    phrase_index: number | null
    text: string
    image: string
    default: string
    commented: string | null
    voice_gender: VoiceGender
    voice_profile: string | null
    due: string | null
    state: number | null
    reps: number | null
  }>
  total: number
  dueCount: number
  newCount: number
  nextDueAt: string | null
  progress: CardsDeckProgress
}

export interface GradeRequest {
  card_key: string
  mode: StudyMode
  grade: Grade
}

export interface DashboardPoint {
  date: string
  value: number
}

export interface DashboardActivityDay {
  date: string
  reviews: number
  learned: number
}

export interface DashboardMilestone {
  percent: number
  etaDays: number | null
  date: string | null
}

export type ReviewBucketKey = 'overdue' | 'today' | 'tomorrow' | 'threeDays' | 'week' | 'twoWeeks' | 'month' | 'later'

export interface DashboardReviewBucket {
  key: ReviewBucketKey
  count: number
}

export interface CardsDashboardTotals {
  cards: number
  learned: number
  breakdown: {new: number; learning: number; review: number; relearning: number}
}

export interface CardsDashboardDeck {
  mode: StudyMode
  level: number
  unit: number | null
  source: string | null
  n: number
  m: number
  percent: number
  dueCount: number
  newCount: number
  etaDays: number | null
  date: string | null
  memory: Omit<CardsDashboardMemory, 'decayForecast'>
}

export interface CardsDashboardPace {
  perDay: number
  trendPerDay: number
  longRunPerDay: number
}

export interface CardsDashboardMemory {
  avgRetrievability: number
  halfLifeDays: number | null
  decayForecast: DashboardPoint[]
  buckets: {strong: number; steady: number; fragile: number}
  lapsesCards: number
  dueNow: number
  nextReviews: DashboardReviewBucket[]
}

export interface CardsDashboardResponse {
  eligible: boolean
  generatedAt: string
  firstStudyAt: string | null
  totals: CardsDashboardTotals
  currentDeck: CardsDashboardDeck | null
  pace: CardsDashboardPace
  timeline: {
    past: DashboardPoint[]
    future: DashboardPoint[]
    optimistic: DashboardPoint[]
    pessimistic: DashboardPoint[]
    milestones: DashboardMilestone[]
    finishDate: string | null
  }
  activity: DashboardActivityDay[]
  streak: {current: number; longest: number}
  memory: CardsDashboardMemory
  decks: CardsDashboardDeck[]
}
